import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Section,
  Container,
  SectionEyebrow,
  SectionTitle,
  SectionSubtitle,
  CenteredHeader,
  AnimateOnScroll,
} from './HomePage.styles';

// ============================================================================
// CAPABILITY SHOWCASE
// ----------------------------------------------------------------------------
// A crisscross fold (text <-> artefact, alternating) of five cards: a Cross-App
// Build card (three serialised app strips driven by one shared chat, a staged
// data-flow narrative) followed by the four hero proof cards (Source Analysis,
// Data Triangulation, Revenue Projection, Quality Check). The cards are
// DUPLICATED here (not shared with the hero) on purpose, so this can be styled
// and tuned independently.
//
// `loop` (true on /product): cards animate continuously while in view — they
// re-demonstrate their build on a cycle (bars grow, confidence fills, rows
// cascade, badges pop, then reset and repeat). Without `loop`, the build plays
// once on scroll-in. Either way the static end-state is the default, so
// reduced-motion and not-yet-in-view render complete.
// ============================================================================

// Accent is themed per-app via CSS custom properties set on each card root, so
// the PowerPoint (default) output stays pixel-identical to before. RUST etc.
// now resolve to those variables; APP_THEMES supplies the concrete values.
const RUST = 'var(--cap-accent)';
const RUST_TINT = 'var(--cap-accent-tint)';
const APP_THEMES = {
  powerpoint: { accent: '#B83B1D', accentRgb: '184 59 29', accentTint: '#faece7', accentInk: '#B83B1D' },
  excel: { accent: '#217346', accentRgb: '33 115 70', accentTint: '#e6f0ea', accentInk: '#1a5c38' },
  word: { accent: '#35548A', accentRgb: '53 84 138', accentTint: '#e9edf5', accentInk: '#2a4470' },
};
const themeVars = (theme) => ({
  '--cap-accent': theme.accent,
  '--cap-accent-rgb': theme.accentRgb,
  '--cap-accent-tint': theme.accentTint,
  '--cap-accent-ink': theme.accentInk,
});
// ONE cadence for every looping card. The cards used to run on two different
// clocks (three at 5.4s, two at 6.4s), so when several were on screen together
// they drifted permanently out of phase and the section read as restless
// rather than composed. Every cycle length below is derived from this.
const CARD_CYCLE_S = 6;
const CARD_CYCLE_MS = CARD_CYCLE_S * 1000;

const GREEN = '#16a34a';
const GREEN_TINT = '#e1f5ee';
const GREEN_INK = '#0f6e56';
const BLUE_TINT = '#e6f1fb';
const BLUE_INK = '#185fa5';
const AMBER_TINT = '#faeeda';
const AMBER_INK = '#92600c';
const INK = '#2E140A';
const inkA = (a) => `rgba(46, 20, 10, ${a})`;

// One-shot build animations
const growY = keyframes`from { transform: scaleY(0); } to { transform: scaleY(1); }`;
const fillX = keyframes`from { transform: scaleX(0); } to { transform: scaleX(1); }`;
const riseIn = keyframes`from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: none; }`;
const popIn = keyframes`0% { opacity: 0; transform: scale(0.6); } 70% { transform: scale(1.06); } 100% { opacity: 1; transform: scale(1); }`;
const pulseY = keyframes`0%, 100% { transform: scaleY(0.9); opacity: 0.85; } 50% { transform: scaleY(1); opacity: 1; }`;

// Looping ("always-on") variants: continuous motion that never blanks out
// content: bars breathe in a wave, badges/markers softly pulse. Rows/checks
// cascade in once (build) and then STAY.
const waveY = keyframes`0%, 100% { transform: scaleY(0.74); } 50% { transform: scaleY(1); }`;
const softPulse = keyframes`0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); }`;

// ── Source Analysis keyframes ──────────────────────────────────────────────
const srcRowIn = keyframes`0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: none; }`;
const srcSpin = keyframes`0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); }`;
const srcIndeterminate = keyframes`0% { transform: translateX(-100%); } 100% { transform: translateX(220%); }`;
const srcProgressFill = keyframes`0% { transform: scaleX(0); } 100% { transform: scaleX(1); }`;
const srcTickPop = keyframes`0% { opacity: 0; transform: scale(0.4); } 70% { transform: scale(1.15); } 100% { opacity: 1; transform: scale(1); }`;
const srcCycleProgress = keyframes`0% { transform: scaleX(0); } 78% { transform: scaleX(1); } 100% { transform: scaleX(1); }`;
const srcCycleRow = keyframes`0%, 100% { opacity: 1; transform: none; } 96% { opacity: 1; transform: none; } 98% { opacity: 0; transform: translateY(6px); } 99% { opacity: 0; transform: translateY(6px); }`;
const srcCycleSpinner = keyframes`0% { opacity: 1; } 30% { opacity: 1; } 36% { opacity: 0; } 100% { opacity: 0; }`;
const srcCycleTick = keyframes`0% { opacity: 0; transform: scale(0.4); } 30% { opacity: 0; transform: scale(0.4); } 38% { opacity: 1; transform: scale(1.15); } 44%, 100% { opacity: 1; transform: scale(1); }`;
const srcCycleStatus = keyframes`0%, 30% { color: rgb(var(--cap-accent-rgb) / 0.85); } 38%, 100% { color: #0f6e56; }`;
const srcCounterPulse = keyframes`0%, 100% { transform: scale(1); } 50% { transform: scale(1.06); }`;

// ── Data Triangulation keyframes ───────────────────────────────────────────
const triSourceIn = keyframes`0% { opacity: 0; transform: translateX(-10px); } 18% { opacity: 1; transform: translateX(0); } 78% { opacity: 1; transform: translateX(0); } 100% { opacity: 0.92; transform: translateX(0); }`;
const triAssemble = keyframes`0%, 55% { opacity: 0.35; transform: scale(0.94); letter-spacing: 0.02em; } 78% { opacity: 1; transform: scale(1.04); letter-spacing: -0.03em; } 100% { opacity: 1; transform: scale(1); letter-spacing: -0.03em; }`;
const triTickFlash = keyframes`0%, 60% { opacity: 0; transform: scale(0.4); } 78% { opacity: 1; transform: scale(1.15); } 100% { opacity: 1; transform: scale(1); }`;
const triMarkerGlide = keyframes`0% { left: 18%; opacity: 0.6; } 22% { left: 18%; opacity: 1; } 50% { left: 72%; opacity: 1; } 80% { left: 46%; opacity: 1; } 100% { left: 46%; opacity: 1; }`;
const triBadgeLand = keyframes`0%, 75% { opacity: 0; transform: translateY(-6px) scale(0.7); } 88% { opacity: 1; transform: translateY(0) scale(1.08); } 100% { opacity: 1; transform: translateY(0) scale(1); }`;
const triBadgePulse = keyframes`0%, 72%, 100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); } 80% { box-shadow: 0 0 0 6px rgba(22, 163, 74, 0.18); } 92% { box-shadow: 0 0 0 10px rgba(22, 163, 74, 0); }`;
const triLabelFade = keyframes`0%, 60% { opacity: 0; } 80%, 100% { opacity: 1; }`;
const triCaretBlink = keyframes`0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; }`;

// ── Revenue Projection keyframes ───────────────────────────────────────────
// Bars grow in cleanly one by one (left to right). In the loop the whole
// series builds in sequence, holds while the trend draws and the value and
// CAGR settle, then resets and replays on a calm cadence. The per-bar stagger
// (REV_BAR_CYCLE_STEP) shifts each bar's growth window evenly, so the read is a
// normal financial chart building, not erratic motion.
const revBarGrow = keyframes`0% { transform: scaleY(0); } 100% { transform: scaleY(1); }`;
// The loop used to drop every bar to scaleY(0) and rebuild from nothing each
// cycle, so anyone glancing at the card during that window saw an empty or
// half-drawn chart, which read as the chart being wrong. The series now stays
// at full height and only settles back a little before rising again, so the
// figures on screen are always the real ones.
const revBarCycle = keyframes`0% { transform: scaleY(0.82); } 18% { transform: scaleY(1); } 82% { transform: scaleY(1); } 100% { transform: scaleY(0.82); }`;
const revDeltaPop = keyframes`0%, 60% { opacity: 0; transform: scale(0.4) translateY(4px); } 75% { opacity: 1; transform: scale(1.12) translateY(0); } 85%, 96% { opacity: 1; transform: scale(1) translateY(0); } 100% { opacity: 0; transform: scale(1) translateY(0); }`;
const revValueFade = keyframes`0%, 10% { opacity: 0; transform: translateY(4px); } 28%, 92% { opacity: 1; transform: none; } 100% { opacity: 0; transform: translateY(0); }`;
const revStepCycle = keyframes`0%, 22% { opacity: 0.35; } 30%, 88% { opacity: 1; } 96%, 100% { opacity: 0.35; }`;
const revStepBuild = keyframes`from { opacity: 0.35; } to { opacity: 1; }`;

// ── Citations (Word card 3) keyframes ──────────────────────────────────────
// The passage settles, the superscript markers pop in one by one, the numbered
// source list builds, the counter counts up. Loop variants re-run on a calm
// cadence (the build window holds for most of the cycle, then resets).
const citePassageIn = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`;
const citeMarkerPop = keyframes`0% { opacity: 0; transform: translateY(-2px) scale(0.4); } 70% { transform: translateY(0) scale(1.2); } 100% { opacity: 1; transform: translateY(0) scale(1); }`;
const citeSourceIn = keyframes`from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: none; }`;
const CITE_CYCLE_S = CARD_CYCLE_S;
const citeMarkerCycle1 = keyframes`0%, 20% { opacity: 0; transform: translateY(-2px) scale(0.4); } 28%, 100% { opacity: 1; transform: translateY(0) scale(1); }`;
const citeMarkerCycle2 = keyframes`0%, 32% { opacity: 0; transform: translateY(-2px) scale(0.4); } 40%, 100% { opacity: 1; transform: translateY(0) scale(1); }`;
const citeMarkerCycle3 = keyframes`0%, 44% { opacity: 0; transform: translateY(-2px) scale(0.4); } 52%, 100% { opacity: 1; transform: translateY(0) scale(1); }`;
const citeSourceCycle1 = keyframes`0%, 26% { opacity: 0; transform: translateX(-6px); } 34%, 100% { opacity: 1; transform: none; }`;
const citeSourceCycle2 = keyframes`0%, 38% { opacity: 0; transform: translateX(-6px); } 46%, 100% { opacity: 1; transform: none; }`;
const citeSourceCycle3 = keyframes`0%, 50% { opacity: 0; transform: translateX(-6px); } 58%, 100% { opacity: 1; transform: none; }`;

// ── Quality Check keyframes ────────────────────────────────────────────────
const qcSpin = keyframes`to { transform: rotate(360deg); }`;
const qcTickPop = keyframes`0% { opacity: 0; transform: scale(0.3); } 60% { transform: scale(1.18); } 100% { opacity: 1; transform: scale(1); }`;
const qcSpinnerFade = keyframes`0%, 100% { opacity: 1; } 100% { opacity: 0; }`;
const qcLabelShift = keyframes`0%, 100% { color: rgba(46, 20, 10, 0.55); } 50% { color: #2E140A; }`;
const qcRowGlow = keyframes`0%, 100% { background: transparent; } 50% { background: rgb(var(--cap-accent-rgb) / 0.05); }`;
const qcBuildBar = keyframes`0% { transform: scaleX(0); } 100% { transform: scaleX(0.92); }`;
const qcCycleBar = keyframes`0%, 4% { transform: scaleX(0); } 18% { transform: scaleX(0.18); } 34% { transform: scaleX(0.36); } 50% { transform: scaleX(0.54); } 66% { transform: scaleX(0.72); } 82%, 100% { transform: scaleX(0.92); }`;
const qcCycleNum = keyframes`0%, 4% { opacity: 0.4; } 100% { opacity: 1; }`;
const qcCheckCycle1 = keyframes`0%, 14% { opacity: 0; transform: scale(0.3); } 22%, 100% { opacity: 1; transform: scale(1); }`;
const qcCheckCycle2 = keyframes`0%, 30% { opacity: 0; transform: scale(0.3); } 38%, 100% { opacity: 1; transform: scale(1); }`;
const qcCheckCycle3 = keyframes`0%, 46% { opacity: 0; transform: scale(0.3); } 54%, 100% { opacity: 1; transform: scale(1); }`;
const qcCheckCycle4 = keyframes`0%, 62% { opacity: 0; transform: scale(0.3); } 70%, 100% { opacity: 1; transform: scale(1); }`;
const qcCheckCycle5 = keyframes`0%, 78% { opacity: 0; transform: scale(0.3); } 86%, 100% { opacity: 1; transform: scale(1); }`;
const qcSpinCycle1 = keyframes`0%, 12% { opacity: 1; } 18%, 100% { opacity: 0; }`;
const qcSpinCycle2 = keyframes`0%, 28% { opacity: 1; } 34%, 100% { opacity: 0; }`;
const qcSpinCycle3 = keyframes`0%, 44% { opacity: 1; } 50%, 100% { opacity: 0; }`;
const qcSpinCycle4 = keyframes`0%, 60% { opacity: 1; } 66%, 100% { opacity: 0; }`;
const qcSpinCycle5 = keyframes`0%, 76% { opacity: 1; } 82%, 100% { opacity: 0; }`;


const noMotion = { '@media (prefers-reduced-motion: reduce)': { animation: 'none !important' } };

// build vs loop-cycle vs off, for a given element
const stagedAnim = ({ run, loop, build, cycle }) => (run ? (loop ? cycle : build) : 'none');

// Count a number up to `target` once `active` flips true — a confident data
// reveal. Returns the raw animated value; each caller formats it.
function useCountUp(target, active, duration = 1100) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVal(target);
      return undefined;
    }
    let raf;
    let start = null;
    const tick = (ts) => {
      if (start === null) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      setVal(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [target, active, duration]);
  return val;
}

// Renders a number that counts up to `target` once `active` flips true.
const CountUp = ({ target, active, format = (v) => Math.round(v), duration }) => {
  const v = useCountUp(target, active, duration);
  return <>{format(v)}</>;
};

const cardSx = {
  position: 'relative',
  background: '#fff',
  border: `1px solid ${inkA(0.1)}`,
  borderRadius: '16px',
  boxShadow: `0 8px 30px ${inkA(0.07)}`,
  p: 2.5,
  color: INK,
  width: '100%',
  maxWidth: 580,
  minHeight: 300,
  boxSizing: 'border-box',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

// The grounded "stage" the card sits on (the Harvey-style framed visual): a
// warm panel with a faint dot grid + soft gradient and a hairline border, so
// the white card reads as placed, not floating. Fills its half of the row.
const stageSx = {
  width: '100%',
  maxWidth: 640,
  borderRadius: '22px',
  p: { xs: 2, md: '28px' },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${inkA(0.07)}`,
  background: `
    radial-gradient(${inkA(0.05)} 1px, transparent 1px) 0 0 / 17px 17px,
    linear-gradient(155deg, rgb(var(--cap-accent-rgb) / 0.07) 0%, ${inkA(0.012)} 72%)
  `,
  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.55)`,
};

const cardHeaderSx = { display: 'flex', alignItems: 'center', gap: 0.9, mb: 1.25 };
const cardTitleSx = { fontSize: 13, fontWeight: 600, color: INK, letterSpacing: '-0.005em' };
const dotSx = (bg) => ({ width: 7, height: 7, borderRadius: '50%', background: bg, flexShrink: 0 });

const CheckCircle = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
    <circle cx="6" cy="6" r="5.5" fill={GREEN} />
    <path d="M3.5 6l2 2L8.5 4.5" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function alphaRust(a) { return `rgb(var(--cap-accent-rgb) / ${a})`; }

// ── Card 1: Source Analysis ────────────────────────────────────────────────
const SRC_SOURCES = [
  { name: 'Nordic Capital Markets', type: 'Report', value: '$4.2B', tint: RUST_TINT, ink: RUST },
  { name: 'ECB Fintech Report Q3', type: 'Database', value: '23% YoY', tint: BLUE_TINT, ink: BLUE_INK },
  { name: 'Klarna AB Annual', type: 'Filing', value: '$1.8B', tint: AMBER_TINT, ink: AMBER_INK },
  { name: 'Statista Payments', type: 'API', value: '340M', tint: GREEN_TINT, ink: GREEN_INK },
  { name: 'IMF Payments Outlook', type: 'Report', value: '€2.1T', tint: RUST_TINT, ink: RUST },
  { name: 'Adyen Investor Brief', type: 'Filing', value: '$916M', tint: AMBER_TINT, ink: AMBER_INK },
];

const SrcRowStatus = ({ animate, loop, delay }) => {
  // Static end-state: green tick. Build: spinner -> tick. Loop: spinner -> tick on each cycle.
  const cycleDur = CARD_CYCLE_S;
  if (!animate) {
    return <CheckCircle size={12} />;
  }
  if (loop) {
    return (
      <Box sx={{ position: 'relative', width: 12, height: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ position: 'absolute', inset: 0, animation: `${srcCycleSpinner} ${cycleDur}s ease-in-out ${delay}s infinite`, ...noMotion }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${alphaRust(0.22)}`, borderTopColor: RUST, animation: `${srcSpin} 0.8s linear infinite`, ...noMotion }} />
        </Box>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: `${srcCycleTick} ${cycleDur}s ease-in-out ${delay}s infinite`, ...noMotion }}>
          <CheckCircle size={12} />
        </Box>
      </Box>
    );
  }
  // Build (one-shot): spinner briefly, then tick pops in.
  return (
    <Box sx={{ position: 'relative', width: 12, height: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ position: 'absolute', inset: 0, opacity: 1, animation: `fadeOutSrc 0.3s ease ${delay + 0.4}s forwards`, '@keyframes fadeOutSrc': { to: { opacity: 0 } }, ...noMotion }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${alphaRust(0.22)}`, borderTopColor: RUST, animation: `${srcSpin} 0.8s linear infinite`, ...noMotion }} />
      </Box>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: `${srcTickPop} 0.45s ease-out ${delay + 0.55}s both`, ...noMotion }}>
        <CheckCircle size={12} />
      </Box>
    </Box>
  );
};

const SourceAnalysisCard = ({ animate, loop, theme }) => {
  const cycleDur = CARD_CYCLE_S;
  return (
    <Box sx={cardSx} style={themeVars(theme)}>
      {/* Header */}
      <Box sx={cardHeaderSx}>
        <Box sx={dotSx(GREEN)} />
        <Typography component="span" sx={cardTitleSx}>Source Analysis</Typography>
        <Box
          component="span"
          sx={{
            ml: 'auto',
            fontSize: 10.5,
            fontWeight: 600,
            px: 1,
            py: 0.3,
            borderRadius: '999px',
            background: RUST_TINT,
            color: RUST,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            animation: stagedAnim({ run: animate, loop, build: 'none', cycle: `${srcCounterPulse} ${CARD_CYCLE_S}s ease-in-out infinite` }),
            ...noMotion,
          }}
        >
          <CountUp target={87} active={animate} /> sources
        </Box>
      </Box>

      {/* Analyzing progress strip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.1 }}>
        <Typography component="span" sx={{ fontSize: 9.5, fontWeight: 600, color: inkA(0.55), letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Analyzing
        </Typography>
        <Box sx={{ flex: 1, height: 4, borderRadius: '2px', background: RUST_TINT, overflow: 'hidden', position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(90deg, ${RUST}, rgb(var(--cap-accent-rgb) / 0.7))`,
              borderRadius: '2px',
              transformOrigin: 'left',
              transform: 'scaleX(1)',
              animation: stagedAnim({
                run: animate,
                loop,
                build: `${srcProgressFill} 1.6s cubic-bezier(0.16,1,0.3,1) 0.1s both`,
                cycle: `${srcCycleProgress} ${cycleDur}s ease-in-out infinite`,
              }),
              ...noMotion,
            }}
          />
          {animate && loop ? (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: '38%',
                background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)`,
                animation: `${srcIndeterminate} 1.6s ease-in-out infinite`,
                ...noMotion,
              }}
            />
          ) : null}
        </Box>
        <Typography component="span" sx={{ fontSize: 10, fontWeight: 700, color: RUST, fontVariantNumeric: 'tabular-nums', minWidth: 38, textAlign: 'right' }}>
          <CountUp target={87} active={animate} />/87
        </Typography>
      </Box>

      {/* Table header */}
      <Box sx={{ display: 'flex', fontSize: 9.5, color: inkA(0.45), pb: 0.6, borderBottom: `1px solid ${inkA(0.08)}`, letterSpacing: '0.03em', textTransform: 'uppercase', fontWeight: 600 }}>
        <Box component="span" sx={{ width: 14 }} />
        <Box component="span" sx={{ flex: 1 }}>Source</Box>
        <Box component="span" sx={{ width: 70 }}>Type</Box>
        <Box component="span" sx={{ width: 60, textAlign: 'right' }}>Value</Box>
        <Box component="span" sx={{ width: 18, textAlign: 'right' }} />
      </Box>

      {/* Rows */}
      <Box sx={{ position: 'relative' }}>
        {SRC_SOURCES.map((s, i) => {
          const stagger = 0.18 + i * 0.14;
          return (
            <Box
              key={s.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: 11.5,
                py: 0.7,
                borderBottom: i < SRC_SOURCES.length - 1 ? `1px solid ${inkA(0.05)}` : 'none',
                opacity: 1,
                animation: stagedAnim({
                  run: animate,
                  loop,
                  build: `${srcRowIn} 0.45s ease-out ${stagger}s both`,
                  cycle: `${srcCycleRow} ${cycleDur}s ease-in-out ${i * 0.05}s infinite`,
                }),
                ...noMotion,
              }}
            >
              <Box component="span" sx={{ width: 14, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ ...dotSx(inkA(0.25)), width: 5, height: 5 }} />
              </Box>
              <Box
                component="span"
                sx={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  pr: 1,
                  fontWeight: 500,
                  animation: stagedAnim({ run: animate, loop, build: 'none', cycle: `${srcCycleStatus} ${cycleDur}s ease-in-out ${i * 0.05}s infinite` }),
                  ...noMotion,
                }}
              >
                {s.name}
              </Box>
              <Box component="span" sx={{ width: 70 }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 600,
                    px: 0.8,
                    py: 0.25,
                    borderRadius: '5px',
                    background: s.tint,
                    color: s.ink,
                    letterSpacing: '0.02em',
                  }}
                >
                  {s.type}
                </Box>
              </Box>
              <Box component="span" sx={{ width: 60, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{s.value}</Box>
              <Box component="span" sx={{ width: 18, display: 'flex', justifyContent: 'flex-end' }}>
                <SrcRowStatus animate={animate} loop={loop} delay={i * 0.05} />
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Footer summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.1, pt: 1, borderTop: `1px solid ${inkA(0.06)}` }}>
        <CheckCircle size={12} />
        <Typography component="span" sx={{ fontSize: 10.5, color: inkA(0.6), fontWeight: 500 }}>
          All sources analyzed, indexed by topic
        </Typography>
        <Box
          component="span"
          sx={{
            ml: 'auto',
            fontSize: 9.5,
            fontWeight: 600,
            px: 0.9,
            py: 0.25,
            borderRadius: '999px',
            background: GREEN_TINT,
            color: GREEN_INK,
            letterSpacing: '0.02em',
          }}
        >
          Ready
        </Box>
      </Box>
    </Box>
  );
};

// ── Card 2: Data Triangulation ─────────────────────────────────────────────
const TRI_SOURCES = [
  { name: 'Nordic Capital Markets', value: '$4.2B', fromX: '-150px', fromY: '0px', anchorTop: '38%' },
  { name: 'ECB Fintech Report Q3', value: '$4.1B', fromX: '150px', fromY: '-30px', anchorTop: '54%' },
  { name: 'Dealroom Nordic Data', value: '$4.3B', fromX: '150px', fromY: '30px', anchorTop: '70%' },
];

const DataTriangulationCard = ({ animate, loop, theme }) => (
  <Box sx={cardSx} style={themeVars(theme)}>
    <Box sx={cardHeaderSx}>
      <Box sx={dotSx(GREEN)} />
      <Typography component="span" sx={cardTitleSx}>Data Triangulation</Typography>
      <Box component="span" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 10, fontWeight: 600, px: 0.9, py: 0.25, borderRadius: '999px', background: RUST_TINT, color: RUST }}>
        <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', background: RUST, animation: animate && loop ? `${softPulse} 1.4s ease-in-out infinite` : 'none', ...noMotion }} />
        cross-checking 3 sources
      </Box>
    </Box>

    {/* Hero: the converging headline value + verified badge */}
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.4, minHeight: 44 }}>
      <Typography
        component="span"
        sx={{
          fontSize: 38, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1, color: INK,
          fontVariantNumeric: 'tabular-nums',
          animation: stagedAnim({
            run: animate, loop,
            build: `${triAssemble} 1.2s cubic-bezier(0.16,1,0.3,1) 0.6s both`,
            cycle: `${triAssemble} ${CARD_CYCLE_S}s ease-in-out infinite`,
          }),
          ...noMotion,
        }}
      >
        <CountUp target={4.2} active={animate} format={(v) => `$${v.toFixed(1)}B`} />
      </Typography>
      <Box
        component="span"
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: 11, fontWeight: 600,
          color: GREEN_INK, background: GREEN_TINT, px: 1, py: 0.35, borderRadius: '999px',
          animation: stagedAnim({
            run: animate, loop,
            build: `${triBadgeLand} 0.7s cubic-bezier(0.16,1,0.3,1) 1s both`,
            cycle: `${triBadgeLand} ${CARD_CYCLE_S}s ease-in-out infinite, ${triBadgePulse} ${CARD_CYCLE_S}s ease-in-out infinite`,
          }),
          ...noMotion,
        }}
      >
        <CheckCircle size={13} /> Verified
      </Box>
      {/* Tiny caret hint of "agent at work" — only in loop */}
      {animate && loop && (
        <Box component="span" sx={{ position: 'absolute', right: 0, top: 4, width: 2, height: 18, background: alphaRust(0.55), animation: `${triCaretBlink} 1s steps(2) infinite`, ...noMotion }} />
      )}
    </Box>
    <Typography component="div" sx={{ fontSize: 10.5, color: inkA(0.5), mb: 1.1, letterSpacing: '0.01em' }}>
      Nordic Fintech TAM, consensus of 3 independent sources
    </Typography>

    {/* Source rows that triangulate the figure, each resolving to a tick */}
    <Box sx={{ position: 'relative' }}>
      {/* Source rows */}
      {TRI_SOURCES.map((s, i) => (
        <Box
          key={s.name}
          sx={{
            display: 'flex', alignItems: 'center', fontSize: 12, py: 0.65, position: 'relative',
            borderBottom: i < TRI_SOURCES.length - 1 ? `1px solid ${inkA(0.05)}` : 'none',
            animation: stagedAnim({
              run: animate, loop,
              build: `${riseIn} 0.45s ease-out ${0.2 + i * 0.12}s both`,
              cycle: `${triSourceIn} ${CARD_CYCLE_S}s ease-in-out ${i * 0.18}s infinite`,
            }),
            ...noMotion,
          }}
        >
          <Box sx={{ ...dotSx(RUST), width: 6, height: 6, mr: 1 }} />
          <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pr: 1, color: INK }}>{s.name}</Box>
          <Box component="span" sx={{ fontWeight: 700, mr: 0.9, fontVariantNumeric: 'tabular-nums', color: INK }}>{s.value}</Box>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              animation: stagedAnim({
                run: animate, loop,
                build: `${triTickFlash} 0.5s cubic-bezier(0.16,1,0.3,1) ${0.55 + i * 0.12}s both`,
                cycle: `${triTickFlash} ${CARD_CYCLE_S}s ease-in-out ${i * 0.18}s infinite`,
              }),
              ...noMotion,
            }}
          >
            <CheckCircle size={12} />
          </Box>
        </Box>
      ))}
    </Box>

    {/* Consensus range bar */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.4 }}>
      <Typography component="span" sx={{ fontSize: 9.5, color: inkA(0.5), fontWeight: 500, whiteSpace: 'nowrap' }}>$4.0B</Typography>
      <Box sx={{
        flex: 1, height: 8, borderRadius: '4px', position: 'relative',
        background: `linear-gradient(90deg, ${GREEN_TINT}, ${GREEN}66, ${GREEN_TINT})`,
      }}>
        {/* Ghost markers showing each source's position before convergence */}
        {animate && loop && TRI_SOURCES.map((s, i) => (
          <Box
            key={s.name}
            aria-hidden="true"
            sx={{
              position: 'absolute', top: -1, width: 4, height: 10, borderRadius: '2px',
              background: alphaRust(0.45),
              left: `${[36, 42, 54][i]}%`,
              animation: `${triSourceIn} ${CARD_CYCLE_S}s ease-in-out ${i * 0.18}s infinite`,
              ...noMotion,
            }}
          />
        ))}
        {/* Consensus marker — glides between source positions in loop, then locks at 46% */}
        <Box
          sx={{
            position: 'absolute', left: '46%', top: -3, width: 14, height: 14, borderRadius: '50%',
            background: GREEN, border: '2px solid #fff', transform: 'translateX(-50%)',
            boxShadow: `0 0 0 2px ${GREEN}33`,
            animation: stagedAnim({
              run: animate, loop,
              build: `${popIn} 0.5s ease-out 1.1s both`,
              cycle: `${triMarkerGlide} ${CARD_CYCLE_S}s cubic-bezier(0.65,0,0.35,1) infinite, ${softPulse} ${CARD_CYCLE_S / 2}s ease-in-out 0.4s infinite`,
            }),
            ...noMotion,
          }}
        />
      </Box>
      <Typography component="span" sx={{ fontSize: 9.5, color: inkA(0.5), fontWeight: 500, whiteSpace: 'nowrap' }}>$4.4B</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.7 }}>
      <Typography
        component="span"
        sx={{
          fontSize: 10.5, fontWeight: 700, color: GREEN_INK,
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          animation: stagedAnim({
            run: animate, loop,
            build: `${riseIn} 0.4s ease-out 1.2s both`,
            cycle: `${triLabelFade} ${CARD_CYCLE_S}s ease-in-out infinite`,
          }),
          ...noMotion,
        }}
      >
        <CheckCircle size={11} /> 3 of 3 sources agree, ±2.4%
      </Typography>
      <Typography component="span" sx={{ fontSize: 9.5, color: inkA(0.45), fontVariantNumeric: 'tabular-nums' }}>
        consensus locked
      </Typography>
    </Box>
  </Box>
);

// ── Card 3: Revenue Projection ─────────────────────────────────────────────
const REVENUE_BARS = [
  { h: 52, m: '2024' }, { h: 64, m: '2025' }, { h: 78, m: '2026E', accent: true },
  { h: 90, m: '2027E' }, { h: 100, m: '2028E' },
];

const revBarColor = (b, i, total) => (b.accent ? RUST : (i >= total - 3 ? 'rgb(var(--cap-accent-rgb) / 0.24)' : 'rgb(var(--cap-accent-rgb) / 0.35)'));
const REV_CYCLE_MS = CARD_CYCLE_MS;
// Build (one-shot) per-bar stagger: bars grow left to right.
const REV_BAR_STEP_MS = 220;
// Loop per-bar negative delay: shifts each bar's growth window later in the
// shared cycle so the series builds strictly left to right, then they all hold
// together while the trend draws. Kept small so the whole series is up well
// before the trend begins (revBarCycle reaches full by ~16% + 4 steps).
const REV_BAR_CYCLE_STEP_MS = 120;
const REV_STEPS = [
  { label: 'Pulled actuals', tint: BLUE_TINT, ink: BLUE_INK },
  { label: 'Built the model', tint: AMBER_TINT, ink: AMBER_INK },
  { label: 'Projected to exit', tint: RUST_TINT, ink: RUST },
];

const RevenueProjectionCard = ({ animate, loop, theme }) => {
  const total = REVENUE_BARS.length;
  return (
    <Box sx={{ ...cardSx }} style={themeVars(theme)}>
      <Box sx={cardHeaderSx}>
        <Box sx={dotSx(RUST)} />
        <Typography component="span" sx={cardTitleSx}>EBITDA Projection</Typography>
        <Box
          component="span"
          sx={{
            ml: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.3,
            fontSize: 10.5,
            fontWeight: 700,
            color: GREEN_INK,
            background: GREEN_TINT,
            px: 1,
            py: 0.35,
            borderRadius: '999px',
            opacity: 1,
            transform: 'none',
            animation: stagedAnim({
              run: animate,
              loop,
              build: `${popIn} 0.5s ease-out 1.4s both`,
              cycle: `${revDeltaPop} ${REV_CYCLE_MS}ms ease-in-out infinite`,
            }),
            ...noMotion,
          }}
        >
          {'↑'} <CountUp target={23} active={animate} format={(v) => Math.round(v)} />% CAGR
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.2, mb: 0.2 }}>
        <Typography
          component="div"
          sx={{
            fontSize: 36,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            color: INK,
            fontVariantNumeric: 'tabular-nums',
            opacity: 1,
            animation: stagedAnim({
              run: animate,
              loop,
              build: 'none',
              cycle: `${revValueFade} ${REV_CYCLE_MS}ms ease-in-out infinite`,
            }),
            ...noMotion,
          }}
        >
          $<CountUp target={142} active={animate} format={(v) => Math.round(v)} />M
        </Typography>
        <Typography component="span" sx={{ fontSize: 11, color: inkA(0.5), fontWeight: 500 }}>
          2028E exit
        </Typography>
      </Box>
      <Typography component="div" sx={{ fontSize: 11, color: inkA(0.5), mt: 0.4, mb: 1.4 }}>
        Projected EBITDA, 5-year hold
      </Typography>

      {/* Agent steps row: shows the working sequence */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1.4 }}>
        {REV_STEPS.map((s, i) => (
          <Box
            key={s.label}
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.4,
              fontSize: 9.5,
              fontWeight: 600,
              px: 0.85,
              py: 0.25,
              borderRadius: '999px',
              background: s.tint,
              color: s.ink,
              opacity: 1,
              animation: stagedAnim({
                run: animate,
                loop,
                build: `${revStepBuild} 0.4s ease-out ${0.1 + i * 0.18}s both`,
                cycle: `${revStepCycle} ${REV_CYCLE_MS}ms ease-in-out ${i * 0.35}s infinite`,
              }),
              ...noMotion,
            }}
          >
            <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', background: s.ink, opacity: 0.7 }} />
            {s.label}
          </Box>
        ))}
      </Box>

      {/* Chart area: bars + svg trend line overlay */}
      <Box sx={{ position: 'relative', height: 130, mt: 'auto' }}>
        {/* Faint baseline grid */}
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {[0.25, 0.5, 0.75].map((t) => (
            <Box
              key={t}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${t * 100}%`,
                height: '1px',
                background: inkA(0.05),
              }}
            />
          ))}
        </Box>

        {/* Bars */}
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          {REVENUE_BARS.map((b, i) => {
            const isProjected = i >= total - 3;
            const baseColor = revBarColor(b, i, total);
            return (
              <Box key={b.m} sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', height: '100%', position: 'relative' }}>
                <Box
                  sx={{
                    position: 'relative',
                    // Column-relative width (capped) instead of a fixed 22px:
                    // at 22px the bars read as thin sticks stranded in wide
                    // columns rather than as a chart. The 1.22 height factor
                    // lets the tallest bar reach the top of the 130px plot
                    // area instead of stopping 25px short.
                    width: '58%',
                    maxWidth: 40,
                    height: `${Math.round(b.h * 1.22)}px`,
                    borderRadius: '4px 4px 0 0',
                    background: baseColor,
                    border: isProjected ? `1.2px dashed ${alphaRust(0.55)}` : 'none',
                    backgroundClip: 'padding-box',
                    transformOrigin: 'bottom',
                    transform: 'scaleY(1)',
                    overflow: 'hidden',
                    animation: stagedAnim({
                      run: animate,
                      loop,
                      build: `${revBarGrow} 0.5s cubic-bezier(0.2, 0.85, 0.35, 1) ${i * (REV_BAR_STEP_MS / 1000)}s both`,
                      cycle: `${revBarCycle} ${REV_CYCLE_MS}ms ease-in-out ${i * (REV_BAR_CYCLE_STEP_MS / 1000)}s infinite`,
                    }),
                    ...noMotion,
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {/* Trend-line overlay removed: the drawn line + endpoint dot read as
            visual noise over the bars. The bars carry the projection story. */}
      </Box>

      {/* Period labels */}
      <Box sx={{ display: 'flex', gap: 1, mt: 0.7 }}>
        {REVENUE_BARS.map((b, i) => {
          const isProjected = i >= total - 3;
          return (
            <Typography
              key={b.m}
              component="span"
              sx={{
                flex: 1,
                textAlign: 'center',
                fontSize: 9.5,
                color: b.accent ? RUST : isProjected ? alphaRust(0.6) : inkA(0.45),
                fontWeight: b.accent || isProjected ? 600 : 500,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {b.m}
            </Typography>
          );
        })}
      </Box>

      {/* Footer hairline + legend */}
      <Box sx={{ height: '1px', background: inkA(0.07), mt: 1.2, mb: 0.9 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, fontSize: 10, color: inkA(0.55) }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 8, borderRadius: '2px', background: 'rgb(var(--cap-accent-rgb) / 0.35)' }} />
          <Box component="span">Actual</Box>
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 8, borderRadius: '2px', background: 'rgb(var(--cap-accent-rgb) / 0.24)', border: `1px dashed ${alphaRust(0.55)}`, boxSizing: 'border-box' }} />
          <Box component="span">Projected</Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Card 3 (Word): Citations ───────────────────────────────────────────────
// Word-native artefact for "Cites every figure": a short passage of document
// text with inline superscript markers ([1] [2] [3]) that resolve to a small
// numbered source list, plus a counter. Replaces the EBITDA chart, which does
// not fit Word.
const CITE_SOURCES = [
  { n: 1, label: 'Nordic Capital Markets, 2024', note: 'p. 12' },
  { n: 2, label: 'ECB Fintech Report, Q3', note: 'tbl. 4' },
  { n: 3, label: 'Klarna AB Annual Filing', note: 'p. 31' },
];
// The passage, split so each [n] marker can pop in independently.
const CITE_PASSAGE = [
  { text: 'The Nordic fintech market reached ' },
  { text: '$4.2B in 2024', strong: true },
  { marker: 1 },
  { text: ', growing 23% year over year' },
  { marker: 2 },
  { text: '. Payment volumes are projected to triple by the 2028 exit horizon' },
  { marker: 3 },
  { text: '.' },
];
const citeMarkerAnim = ({ animate, loop, n }) => {
  const cycleByN = { 1: citeMarkerCycle1, 2: citeMarkerCycle2, 3: citeMarkerCycle3 };
  const buildDelay = { 1: 1.0, 2: 1.35, 3: 1.7 }[n];
  return stagedAnim({
    run: animate, loop,
    build: `${citeMarkerPop} 0.5s cubic-bezier(0.16,1,0.3,1) ${buildDelay}s both`,
    cycle: `${cycleByN[n]} ${CITE_CYCLE_S}s ease-in-out infinite`,
  });
};
const citeSourceAnim = ({ animate, loop, i }) => {
  const cycleByI = [citeSourceCycle1, citeSourceCycle2, citeSourceCycle3];
  return stagedAnim({
    run: animate, loop,
    build: `${citeSourceIn} 0.45s ease-out ${1.3 + i * 0.22}s both`,
    cycle: `${cycleByI[i]} ${CITE_CYCLE_S}s ease-in-out infinite`,
  });
};

const CitationMarker = ({ n, animate, loop }) => (
  <Box
    component="sup"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 8.5,
      fontWeight: 700,
      lineHeight: 1,
      color: RUST,
      background: RUST_TINT,
      borderRadius: '3px',
      px: 0.3,
      py: 0.1,
      mx: 0.15,
      verticalAlign: 'super',
      transformOrigin: 'center bottom',
      animation: citeMarkerAnim({ animate, loop, n }),
      animationDelay: animate && loop ? '-4.6s' : undefined,
      ...noMotion,
    }}
  >
    [{n}]
  </Box>
);

const CitationsCard = ({ animate, loop, theme }) => (
  <Box sx={cardSx} style={themeVars(theme)}>
    {/* Header */}
    <Box sx={cardHeaderSx}>
      <Box sx={dotSx(GREEN)} />
      <Typography component="span" sx={cardTitleSx}>Citations</Typography>
      <Box
        component="span"
        sx={{
          ml: 'auto',
          fontSize: 10.5,
          fontWeight: 600,
          px: 1,
          py: 0.3,
          borderRadius: '999px',
          background: RUST_TINT,
          color: RUST,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <CountUp target={12} active={animate} /> citations
      </Box>
    </Box>

    {/* Document passage with inline superscript markers */}
    <Box
      sx={{
        fontSize: 13.5,
        lineHeight: 1.7,
        color: INK,
        letterSpacing: '0.002em',
        mb: 1.4,
        animation: stagedAnim({
          run: animate, loop,
          build: `${citePassageIn} 0.5s ease-out 0.1s both`,
          cycle: 'none',
        }),
        ...noMotion,
      }}
    >
      {CITE_PASSAGE.map((part, i) => {
        if (part.marker) {
          return <CitationMarker key={`m-${part.marker}`} n={part.marker} animate={animate} loop={loop} />;
        }
        return (
          <Box
            component="span"
            key={`t-${i}`}
            sx={part.strong ? { fontWeight: 700, fontVariantNumeric: 'tabular-nums' } : undefined}
          >
            {part.text}
          </Box>
        );
      })}
    </Box>

    <Box sx={{ height: '1px', background: inkA(0.08), mb: 1 }} />

    {/* Numbered source list that builds beneath the passage */}
    <Typography component="div" sx={{ fontSize: 9.5, fontWeight: 600, color: inkA(0.5), letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.8 }}>
      Sources
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.55 }}>
      {CITE_SOURCES.map((s, i) => (
        <Box
          key={s.n}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: 11.5,
            animation: citeSourceAnim({ animate, loop, i }),
            animationDelay: animate && loop ? '-4.6s' : undefined,
            ...noMotion,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 16,
              height: 16,
              flexShrink: 0,
              borderRadius: '4px',
              background: RUST_TINT,
              color: RUST,
              fontSize: 9.5,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {s.n}
          </Box>
          <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: INK, fontWeight: 500 }}>
            {s.label}
          </Box>
          <Box component="span" sx={{ fontSize: 10, color: inkA(0.45), fontVariantNumeric: 'tabular-nums' }}>{s.note}</Box>
          <CheckCircle size={12} />
        </Box>
      ))}
    </Box>

    {/* Footer summary */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.3, pt: 1, borderTop: `1px solid ${inkA(0.06)}` }}>
      <CheckCircle size={12} />
      <Typography component="span" sx={{ fontSize: 10.5, color: inkA(0.6), fontWeight: 500 }}>
        Figures link back to their sources
      </Typography>
      <Box
        component="span"
        sx={{
          ml: 'auto',
          fontSize: 9.5,
          fontWeight: 600,
          px: 0.9,
          py: 0.25,
          borderRadius: '999px',
          background: GREEN_TINT,
          color: GREEN_INK,
          letterSpacing: '0.02em',
        }}
      >
        Cited
      </Box>
    </Box>
  </Box>
);

// ── Card 4: Quality Check ──────────────────────────────────────────────────
// Quality Check items are app-native: PowerPoint audits a deck, Excel audits a
// model, Word audits a document. All three lists are length 6 so the staged
// per-row animations (which index by position) line up unchanged.
const QC_CHECKS_BY_APP = {
  powerpoint: [
    'Font sizes ≥ 14pt',
    'No overlapping elements',
    'Color contrast OK',
    'Data sources cited',
    'Numbers match model',
    'Brand template applied',
  ],
  excel: [
    'No hardcoded inputs',
    'Formulas trace clean',
    'No circular references',
    'Balances tie out',
    'Inputs sourced',
    'Scenarios linked',
  ],
  word: [
    'Defined terms consistent',
    'Cross-references valid',
    'Figures cited',
    'Tracked changes clean',
    'House style applied',
    'Numbering intact',
  ],
};
const QC_CYCLE_S = CARD_CYCLE_S;

const QcSpinner = ({ size = 15, spinAnim }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{
      width: size, height: size, borderRadius: '50%',
      border: `1.8px solid ${alphaRust(0.22)}`,
      borderTopColor: RUST,
      display: 'inline-block', flexShrink: 0,
      animation: spinAnim,
      ...noMotion,
    }}
  />
);

const QualityCheckCard = ({ animate, loop, theme, app = 'powerpoint' }) => {
  const cycleDur = `${QC_CYCLE_S}s`;
  const QC_CHECKS = QC_CHECKS_BY_APP[app] || QC_CHECKS_BY_APP.powerpoint;
  return (
    <Box sx={cardSx} style={themeVars(theme)}>
      {/* Header: title + live "running" indicator */}
      <Box sx={cardHeaderSx}>
        <Box sx={dotSx(GREEN)} />
        <Typography component="span" sx={cardTitleSx}>Quality Check</Typography>
        <Box
          component="span"
          sx={{
            ml: 'auto',
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            fontSize: 10, fontWeight: 600,
            px: 0.9, py: 0.25, borderRadius: '999px',
            background: RUST_TINT, color: RUST,
          }}
        >
          <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', background: RUST }} />
          {QC_CHECKS.length} checks
        </Box>
      </Box>

      {/* Check rows: each has a spinner that fades into a tick. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15 }}>
        {QC_CHECKS.map((label, i) => {
          const tickCycleAnim = [qcCheckCycle1, qcCheckCycle2, qcCheckCycle3, qcCheckCycle4, qcCheckCycle5, qcCheckCycle5][i];
          const spinCycleAnim = [qcSpinCycle1, qcSpinCycle2, qcSpinCycle3, qcSpinCycle4, qcSpinCycle5, qcSpinCycle5][i];
          const realTick = stagedAnim({
            run: animate, loop,
            build: `${qcTickPop} 0.45s cubic-bezier(0.16,1,0.3,1) ${0.35 + i * 0.22}s both`,
            cycle: `${tickCycleAnim} ${cycleDur} ease-in-out infinite`,
          });
          const realSpin = stagedAnim({
            run: animate, loop,
            build: `${qcSpin} 0.9s linear ${i * 0.22}s ${Math.ceil((0.35 + i * 0.22) / 0.9)}, ${qcSpinnerFade} 0.3s ease-out ${0.32 + i * 0.22}s forwards`,
            cycle: `${qcSpin} 1.1s linear infinite, ${spinCycleAnim} ${cycleDur} ease-in-out infinite`,
          });
          const labelAnim = stagedAnim({
            run: animate, loop,
            build: 'none',
            cycle: `${qcRowGlow} ${cycleDur} ease-in-out infinite`,
          });
          return (
            <Box
              key={label}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                fontSize: 13, py: 0.7, px: 0.6,
                borderRadius: '6px',
                animation: labelAnim,
                animationDelay: animate && loop ? `${-QC_CYCLE_S + 0.05 + i * 0.18}s` : undefined,
                ...noMotion,
              }}
            >
              {/* Status icon stack: spinner + tick share a slot */}
              <Box sx={{ position: 'relative', width: 16, height: 16, flexShrink: 0 }}>
                {/* Spinner (rust) */}
                <Box
                  sx={{
                    position: 'absolute', inset: 0,
                    opacity: animate ? (loop ? 1 : 0) : 0,
                    animation: realSpin,
                    animationDelay: animate && loop ? `${-QC_CYCLE_S + i * 0.18}s, ${-QC_CYCLE_S + i * 0.18}s` : undefined,
                    ...noMotion,
                  }}
                >
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      width: 15, height: 15, borderRadius: '50%',
                      border: `1.8px solid ${alphaRust(0.22)}`,
                      borderTopColor: RUST,
                      display: 'inline-block',
                    }}
                  />
                </Box>
                {/* Green tick (final) */}
                <Box
                  sx={{
                    position: 'absolute', inset: 0,
                    opacity: animate && loop ? 0 : 1,
                    animation: realTick,
                    animationDelay: animate && loop ? `${-QC_CYCLE_S + 0.1 + i * 0.18}s` : undefined,
                    transformOrigin: 'center',
                    ...noMotion,
                  }}
                >
                  <CheckCircle size={15} />
                </Box>
              </Box>
              <Box component="span" sx={{ color: INK, fontWeight: 500 }}>{label}</Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ height: '1px', background: inkA(0.1), my: 1.1 }} />

      {/* Confidence row + final resolved tick */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Typography component="span" sx={{ fontSize: 11, color: inkA(0.55), fontWeight: 500 }}>
          Confidence
        </Typography>
        <Box sx={{ flex: 1, height: 7, borderRadius: '4px', background: RUST_TINT, overflow: 'hidden', position: 'relative' }}>
          <Box
            sx={{
              height: '100%', width: '100%',
              background: `linear-gradient(90deg, ${RUST}, rgb(var(--cap-accent-rgb) / 0.72))`,
              borderRadius: '4px',
              transformOrigin: 'left',
              transform: 'scaleX(0.92)',
              animation: stagedAnim({
                run: animate, loop,
                build: `${qcBuildBar} 1.6s cubic-bezier(0.16,1,0.3,1) 0.25s both`,
                cycle: `${qcCycleBar} ${cycleDur} cubic-bezier(0.5,0,0.5,1) infinite`,
              }),
              animationDelay: animate && loop ? `${-QC_CYCLE_S + 0.05}s` : undefined,
              ...noMotion,
            }}
          />
        </Box>
        <Typography
          component="span"
          sx={{
            fontSize: 13, fontWeight: 700, color: RUST,
            fontVariantNumeric: 'tabular-nums',
            minWidth: 36, textAlign: 'right',
            animation: stagedAnim({
              run: animate, loop,
              build: 'none',
              cycle: `${qcCycleNum} ${cycleDur} ease-in-out infinite`,
            }),
            animationDelay: animate && loop ? `${-QC_CYCLE_S + 0.05}s` : undefined,
            ...noMotion,
          }}
        >
          <CountUp target={92} active={animate} format={(v) => `${Math.round(v)}%`} />
        </Typography>

        {/* Final resolved tick: appears once all checks pass */}
        <Box
          component="span"
          sx={{
            display: 'inline-flex', alignItems: 'center',
            opacity: animate && loop ? 0 : 1,
            animation: stagedAnim({
              run: animate, loop,
              build: `${qcTickPop} 0.45s cubic-bezier(0.16,1,0.3,1) ${0.35 + QC_CHECKS.length * 0.22}s both`,
              cycle: `${qcCheckCycle5} ${cycleDur} ease-in-out infinite`,
            }),
            animationDelay: animate && loop ? `${-QC_CYCLE_S + 0.1}s` : undefined,
            ...noMotion,
          }}
        >
          <CheckCircle size={16} />
        </Box>
      </Box>
    </Box>
  );
};

// ── Row 1 card: Cross-App Build ────────────────────────────────────────────
// A serial "movie", one full Office window at a time: scene 1 the user builds
// the model in Excel, scene 2 they write the thesis in Word, scene 3 they ask
// the shared chat in PowerPoint to pull the research and model in and the slide
// builds. Shown one after another (not all at once) so each reads clearly.
const BAND_APPS = {
  excel: { label: 'Excel', file: 'Model.xlsx', letter: 'X', color: '#217346' },
  word: { label: 'Word', file: 'Memo.docx', letter: 'W', color: '#35548A' },
  powerpoint: { label: 'PowerPoint', file: 'Deck.pptx', letter: 'P', color: '#B83B1D' },
};
const BAND_HEAD = 'Build the deck from your live model.';
const BAND_BODY = {
  powerpoint: 'The numbers from your model and the thesis from your memo, laid into your live deck in one chat. Nothing exported.',
  excel: 'The numbers from your model and the thesis from your memo, laid into your live deck in one chat, with no manual export step.',
  word: 'The numbers from your model and the thesis from your memo, laid into your live deck in one chat, with no manual export step.',
};

// Scene transition, message send, and agent status keyframes.
const sceneFade = keyframes`from { opacity: 0; transform: translateY(10px) scale(0.985); } to { opacity: 1; transform: none; }`;
const sceneSend = keyframes`from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; }`;
const sceneStatusOut = keyframes`0%, 80% { opacity: 1; } 90%, 100% { opacity: 0; }`;
const sceneStatusIn = keyframes`0%, 80% { opacity: 0; } 90%, 100% { opacity: 1; }`;

const SCENE_MS = [4200, 3800, 6600];
function useScene(animate) {
  const last = SCENE_MS.length - 1;
  const [i, setI] = useState(last);
  useEffect(() => {
    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduce) { setI(last); return undefined; }
    let idx = 0;
    setI(0);
    let t;
    const tick = () => { t = setTimeout(() => { idx = (idx + 1) % SCENE_MS.length; setI(idx); tick(); }, SCENE_MS[idx]); };
    tick();
    return () => clearTimeout(t);
  }, [animate, last]);
  return i;
}

// The shared Restack task pane: the user message slides up (sent, not typed),
// then the agent works and resolves. Used by every scene.
const SceneSidebar = ({ message, working, done, statusDur = 3.4 }) => (
  <Box sx={{ flex: '1 1 40%', minWidth: 0, display: 'flex', flexDirection: 'column', borderLeft: `1px solid ${inkA(0.1)}`, pl: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.7, pb: 0.6, borderBottom: `1px solid ${inkA(0.07)}` }}>
      <Box sx={{ width: 16, height: 16, flexShrink: 0, borderRadius: '4px', background: RUST_TINT, color: RUST, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box component="svg" viewBox="0 0 24 24" sx={{ width: 10, height: 10 }}>
          <path fill="currentColor" d="M12.6 18.06c-.36.28-.87.28-1.23 0l-6.15-4.78a.991.991 0 0 0-1.22 0c-.51.4-.51 1.17 0 1.57l6.76 5.26c.72.56 1.73.56 2.46 0l6.76-5.26c.51-.4.51-1.17 0-1.57l-.01-.01a.991.991 0 0 0-1.22 0zm.63-3.02 6.76-5.26c.51-.4.51-1.18 0-1.58l-6.76-5.26c-.72-.56-1.73-.56-2.46 0L4.01 8.21c-.51.4-.51 1.18 0 1.58l6.76 5.26c.72.56 1.74.56 2.46-.01z" />
        </Box>
      </Box>
      <Typography component="span" sx={{ fontSize: 9.5, fontWeight: 700, color: INK }}>Restack</Typography>
    </Box>
    <Box sx={{ background: RUST_TINT, color: RUST, borderRadius: '8px 8px 8px 2px', px: 0.8, py: 0.6, fontSize: 9, fontWeight: 500, lineHeight: 1.4, animation: `${sceneSend} 0.5s cubic-bezier(0.16,1,0.3,1) 0.4s both`, ...noMotion }}>
      {message}
    </Box>
    <Box sx={{ mt: 'auto', position: 'relative', height: 16 }}>
      <Box component="span" sx={{ position: 'absolute', inset: 0, display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: 8.5, fontWeight: 600, color: RUST, animation: `${sceneStatusOut} ${statusDur}s ease both`, ...noMotion }}>
        <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', background: RUST, animation: `${softPulse} 1.2s ease-in-out infinite`, ...noMotion }} /> {working}
      </Box>
      <Box component="span" sx={{ position: 'absolute', inset: 0, display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: 8.5, fontWeight: 700, color: GREEN_INK, animation: `${sceneStatusIn} ${statusDur}s ease both`, ...noMotion }}>
        <CheckCircle size={11} /> {done}
      </Box>
    </Box>
  </Box>
);

// A full app window (brand title bar + content area) used by each scene.
const SceneWindow = ({ appKey, children }) => {
  const a = BAND_APPS[appKey];
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: '10px', border: `1px solid ${inkA(0.12)}`, overflow: 'hidden', boxShadow: `0 14px 32px ${inkA(0.12)}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1, py: 0.6, background: a.color, flexShrink: 0 }}>
        <Box sx={{ width: 14, height: 14, flexShrink: 0, borderRadius: '3px', background: 'rgba(255,255,255,0.24)', color: '#fff', fontSize: 8.5, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{a.letter}</Box>
        <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>{a.file}</Typography>
        <Box component="span" sx={{ ml: 'auto', fontSize: 7.5, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.82)' }}>{a.label.toUpperCase()}</Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, p: 1.1, display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Box>
  );
};

// Scene 1, Excel: the model is open; the user asks Restack to confirm the
// numbers against the sources, and a check lands on each row.
const ExcelScene = () => (
  <SceneWindow appKey="excel">
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 1 }}>
      <Box sx={{ flex: '1 1 60%', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pb: 0.6, mb: 0.7, borderBottom: `1px solid ${inkA(0.08)}` }}>
          <Box component="span" sx={{ fontSize: 8, fontWeight: 700, color: inkA(0.6), border: `1px solid ${inkA(0.2)}`, borderRadius: '3px', px: 0.45, py: 0.05 }}>C5</Box>
          <Box component="span" sx={{ fontStyle: 'italic', fontSize: 8.5, color: inkA(0.4) }}>fx</Box>
          <Box component="span" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 8, color: '#1a5c38', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>=SUMPRODUCT(Adoption, ARPU)</Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 1fr 18px', fontSize: 9, border: `1px solid ${inkA(0.1)}`, borderRadius: '5px', overflow: 'hidden' }}>
          {['Segment', '2026E', 'Source', ''].map((h, i) => (
            <Box key={i} sx={{ px: 0.5, py: 0.35, fontWeight: 700, color: inkA(0.5), background: 'rgba(33,115,70,0.08)', borderBottom: `1px solid ${inkA(0.08)}` }}>{h}</Box>
          ))}
          {[['TAM', '$4.2B', 'SEC'], ['SAM', '$1.8B', 'PitchBook'], ['SOM', '$340M', 'model']].map((row, r) => (
            <React.Fragment key={r}>
              {row.map((cell, c) => (
                <Box key={c} sx={{ px: 0.5, py: 0.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: c === 0 ? inkA(0.72) : c === 1 ? '#1a5c38' : inkA(0.45), fontWeight: c === 0 ? 600 : c === 1 ? 700 : 500, borderBottom: r < 2 ? `1px solid ${inkA(0.05)}` : 'none' }}>{cell}</Box>
              ))}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: r < 2 ? `1px solid ${inkA(0.05)}` : 'none' }}>
                <Box component="span" sx={{ display: 'inline-flex', animation: `${popIn} 0.4s ease ${1.2 + r * 0.3}s both`, ...noMotion }}><CheckCircle size={10} /></Box>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <SceneSidebar message="Research and confirm these numbers." working="Checking 3 sources…" done="Confirmed · sources agree" statusDur={3.4} />
    </Box>
  </SceneWindow>
);

// Scene 2, Word: the user asks Restack to draft the thesis from the model; the
// sentence appears on the page.
const WordScene = () => (
  <SceneWindow appKey="word">
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 1 }}>
      <Box sx={{ flex: '1 1 60%', minWidth: 0, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', background: '#fff', borderRadius: '4px', border: `1px solid ${inkA(0.08)}`, boxShadow: `0 1px 5px ${inkA(0.05)}`, p: 1.1 }}>
          <Box sx={{ height: 5.5, width: '52%', borderRadius: '2px', background: 'rgba(53,84,138,0.5)', mb: 0.9 }} />
          <Box sx={{ fontFamily: 'Georgia, serif', fontSize: 10, lineHeight: 1.5, color: inkA(0.82), mb: 0.9, animation: `${riseIn} 0.6s ease 1.3s both`, ...noMotion }}>
            Nordic payments reach <Box component="span" sx={{ color: '#2B579A', fontWeight: 700 }}>$4.2B by 2026</Box>, +23% YoY.
          </Box>
          {['96%', '90%', '70%'].map((w, i) => (
            <Box key={i} sx={{ height: 4, width: w, borderRadius: '2px', background: inkA(0.08), mb: 0.65, animation: `${riseIn} 0.5s ease ${1.6 + i * 0.18}s both`, ...noMotion }} />
          ))}
        </Box>
      </Box>
      <SceneSidebar message="Pull the numbers from Excel and draft the thesis." working="Reading the open Excel…" done="Drafted in your doc" statusDur={3.2} />
    </Box>
  </SceneWindow>
);

// Scene 3, PowerPoint: the user asks Restack to build the slide from the model
// and thesis; the 16:9 slide assembles.
const PptScene = () => (
  <SceneWindow appKey="powerpoint">
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 1 }}>
      {/* The slide, a real 16:9 canvas */}
      <Box sx={{ flex: '1 1 60%', minWidth: 0, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', aspectRatio: '16 / 9', background: '#fff', border: `1px solid ${inkA(0.12)}`, borderRadius: '5px', boxShadow: `0 2px 10px ${inkA(0.06)}`, p: 1, display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'hidden' }}>
          <Box sx={{ height: 6, width: '64%', borderRadius: '2px', background: alphaRust(0.6), animation: `${riseIn} 0.5s ease 1.3s both`, ...noMotion }} />
          <Box sx={{ height: 3.5, width: '42%', borderRadius: '2px', background: inkA(0.12), animation: `${riseIn} 0.5s ease 1.45s both`, ...noMotion }} />
          <Box sx={{ display: 'flex', gap: 0.9, flex: 1, minHeight: 0, mt: 0.3 }}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 0.4 }}>
              {[30, 50, 70, 100].map((h, i) => (
                <Box key={i} sx={{ flex: 1, height: `${h}%`, borderRadius: '1px 1px 0 0', background: i === 3 ? RUST : alphaRust(0.4), transformOrigin: 'bottom', animation: `${growY} 0.5s ease ${1.6 + i * 0.12}s both`, ...noMotion }} />
              ))}
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' }}>
              {['96%', '80%', '88%'].map((w, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, animation: `${riseIn} 0.45s ease ${2 + i * 0.18}s both`, ...noMotion }}>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: alphaRust(0.6), flexShrink: 0 }} />
                  <Box sx={{ height: 3, width: w, borderRadius: '2px', background: inkA(0.13) }} />
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="span" sx={{ fontSize: 6.5, fontWeight: 700, color: alphaRust(0.85), animation: `${riseIn} 0.45s ease 2.6s both`, ...noMotion }}>[1] [2] cited</Box>
        </Box>
      </Box>
      <SceneSidebar message="Take the numbers from Excel and the thesis from Word, and build the slide." working="Reading Excel + Word…" done="Built · 4 cited" statusDur={4} />
    </Box>
  </SceneWindow>
);

// Half-width card: a serial scene player, one full app window at a time.
const SCENE_KEYS = ['excel', 'word', 'powerpoint'];
const CrossAppCard = ({ animate, theme }) => {
  const scene = useScene(animate);
  return (
    <Box sx={cardSx} style={themeVars(theme)}>
      {/* Header */}
      <Box sx={{ ...cardHeaderSx, mb: 1 }}>
        <Box sx={dotSx(GREEN)} />
        <Typography component="span" sx={cardTitleSx}>Cross-App Build</Typography>
        <Box component="span" sx={{ ml: 'auto', fontSize: 10, fontWeight: 700, color: inkA(0.5), fontVariantNumeric: 'tabular-nums' }}>{scene + 1} / 3</Box>
      </Box>

      {/* Scene stepper: Excel -> Word -> PowerPoint */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.1 }}>
        {SCENE_KEYS.map((k, i) => (
          <React.Fragment key={k}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, opacity: i === scene ? 1 : 0.38, transition: 'opacity 0.35s ease', ...noMotion }}>
              <Box sx={{ width: 14, height: 14, flexShrink: 0, borderRadius: '3px', background: BAND_APPS[k].color, color: '#fff', fontSize: 8, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{BAND_APPS[k].letter}</Box>
              <Typography component="span" sx={{ fontSize: 9.5, fontWeight: i === scene ? 700 : 500, color: INK, whiteSpace: 'nowrap' }}>{BAND_APPS[k].label}</Typography>
            </Box>
            {i < SCENE_KEYS.length - 1 && <Box component="span" sx={{ color: inkA(0.3), fontSize: 11, lineHeight: 1 }}>{'→'}</Box>}
          </React.Fragment>
        ))}
      </Box>

      {/* Stage: one full window at a time, cross-fading */}
      <Box sx={{ position: 'relative', height: 232 }}>
        <Box key={scene} sx={{ position: 'absolute', inset: 0, animation: `${sceneFade} 0.5s ease both`, ...noMotion }}>
          {scene === 0 && <ExcelScene />}
          {scene === 1 && <WordScene />}
          {scene === 2 && <PptScene />}
        </Box>
      </Box>
    </Box>
  );
};

// ── The crisscross rows ────────────────────────────────────────────────────
// Each headline matches its graphic: Source Analysis cites, Data Triangulation
// cross-checks, the EBITDA chart builds, Quality Check self-reviews. App-aware:
// Excel reuses the four PowerPoint cards with model-native copy. Word has no
// financial chart, so it shows the Citations card on "Cites every figure" and
// the Source Analysis card on "Builds in the live file". Tenant residency lives
// in Core Capabilities; the cross-app moment now OPENS the section as the first
// crisscross row, the Cross-App Build card.
// Deliberate proof bar: PowerPoint keeps the "every" guarantee. Excel and Word
// stay softened until those newer surfaces earn the same guarantee.
const CITES_BODY = {
  powerpoint: 'Up to 100 sources per slide. Every chart, cell, and bullet links back to a source you can open, defensible in committee.',
  excel: 'Up to 100 sources per model. Charts, cells, and formula inputs link back to sources you can open, defensible in committee.',
  word: 'Up to 100 sources per document. Figures, tables, and sentences link back to sources you can open, defensible in committee.',
};
const BUILDS_BODY = {
  powerpoint: 'It writes straight into your live deck, on your firm’s template, so there is no rebuild once the thinking is done.',
  excel: 'It builds formulas and cells straight into your live workbook, on your firm’s template, so there is no rebuild once the thinking is done.',
  word: 'It builds the analysis straight into your live document, on your firm’s template, so there is no rebuild once the thinking is done.',
};
const ROWS_BY_APP = (app) => [
  {
    id: 'cap-crossapp', n: 1, Card: CrossAppCard, cardLeft: true,
    title: BAND_HEAD,
    body: BAND_BODY[app] || BAND_BODY.powerpoint,
  },
  {
    id: 'cap-source', n: 2, Card: app === 'word' ? CitationsCard : SourceAnalysisCard, cardLeft: false,
    title: 'Cites every figure',
    body: CITES_BODY[app] || CITES_BODY.powerpoint,
  },
  {
    id: 'cap-triangulation', n: 2, Card: DataTriangulationCard, cardLeft: true,
    title: 'Triangulates the data',
    body: 'Figures are cross-checked against independent sources, and anything that does not reconcile gets flagged instead of shipped.',
  },
  {
    id: 'cap-revenue', n: 3, Card: app === 'word' ? SourceAnalysisCard : RevenueProjectionCard, cardLeft: false,
    title: 'Builds in the live file',
    body: BUILDS_BODY[app] || BUILDS_BODY.powerpoint,
  },
  {
    id: 'cap-quality', n: 4, Card: QualityCheckCard, cardLeft: true,
    title: 'Checks its own work',
    body: 'It reviews its own output against the brief and fixes what is off before you see it: fonts, overlaps, sourcing, layout.',
  },
];

export default function CapabilityShowcase({ isVisible = () => true, loop = false, app = 'powerpoint' }) {
  const theme = APP_THEMES[app] || APP_THEMES.powerpoint;
  const ROWS = ROWS_BY_APP(app);
  return (
    <Section id="capability-showcase" sx={{ pt: { xs: 6, md: 11 } }}>
      <Container>
        <AnimateOnScroll isVisible={isVisible('cap-header')} data-animate-id="cap-header">
          <CenteredHeader>
            <SectionEyebrow>How it works</SectionEyebrow>
            <SectionTitle component="h2">Built for the <em>deliverable a committee accepts</em>.</SectionTitle>
            <SectionSubtitle>
              It builds the model, deck, and memo, carrying the work between them on your firm&rsquo;s templates.
              The apps are where it works; the Private Equity deliverable is what it&rsquo;s built for.
            </SectionSubtitle>
          </CenteredHeader>
        </AnimateOnScroll>

        {/* maxWidth 1100 + centered matches the Core Capabilities bento band
            below, so the two sections line up as one. */}
        <Box sx={{ mt: { xs: 4, md: 8 }, maxWidth: 1320, mx: 'auto', display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 10 } }}>
          {ROWS.map((r) => {
            const vis = isVisible(r.id);
            const Card = r.Card;
            return (
              <AnimateOnScroll key={r.id} isVisible={vis} data-animate-id={r.id}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: { xs: 3, md: 5 },
                    alignItems: 'center',
                  }}
                >
                  {/* TEXT — hugs the OUTER edge of its column so the row spans
                      the full container width, matching the Core Capabilities
                      grid below. */}
                  <Box sx={{ order: { md: r.cardLeft ? 2 : 1 }, display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    {/* Text always hugs the INNER edge of its column (flex-end), so it
                        sits right beside its card instead of flush against the page edge.
                        Cards 1 & 3 (text in the LEFT column) now read as a caption for the
                        graphic on their right, matching cards 2 & 4. */}
                    <Box sx={{ maxWidth: 500 }}>
                      <Typography component="h3" sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.25, mb: 1.25, color: 'text.primary' }}>
                        {r.title}
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 16, md: 18 }, lineHeight: 1.65, color: 'text.secondary' }}>
                        {r.body}
                      </Typography>
                    </Box>
                  </Box>
                  {/* CARD on its grounded stage (framed visual). */}
                  <Box sx={{ order: { md: r.cardLeft ? 1 : 2 }, display: 'flex', justifyContent: { xs: 'flex-start', md: r.cardLeft ? 'flex-start' : 'flex-end' } }}>
                    <Box sx={stageSx} style={themeVars(theme)}>
                      <Card animate={vis} loop={loop} theme={theme} app={app} />
                    </Box>
                  </Box>
                </Box>
              </AnimateOnScroll>
            );
          })}
        </Box>
      </Container>
    </Section>
  );
}
